import { useEffect, useState } from "react";
import { getIdea, putIdea } from "../data/ideaStore";
import type { ModelInfo } from "../data/modelStore";
import {
  type Critique,
  type Idea,
  type Persona,
  type Settings,
  critiquesOf,
  personasOf,
  scanResultOf,
} from "../data/types";
import { activeEngineLabel } from "../engine/active";
import { generateCritiques } from "../engine/critique";
import { generatePersonas } from "../engine/personas";

interface Props {
  ideaId: string;
  settings: Settings;
  installedModel: ModelInfo | null;
  onBack: () => void;
  onIdeaSaved: () => void;
}

type State =
  | { kind: "loading" }
  | { kind: "noIdea" }
  | { kind: "ready"; idea: Idea }
  | { kind: "generatingPersonas"; idea: Idea }
  | { kind: "personasError"; idea: Idea; message: string }
  | { kind: "generatingCritiques"; idea: Idea; done: number; total: number }
  | { kind: "critiquesPartial"; idea: Idea; errors: { personaId: string; message: string }[] };

export function PersonasScreen(props: Props) {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const idea = await getIdea(props.ideaId);
      if (cancelled) return;
      if (!idea) return setState({ kind: "noIdea" });
      setState({ kind: "ready", idea });
    })();
    return () => {
      cancelled = true;
    };
  }, [props.ideaId]);

  const handleGeneratePersonas = async () => {
    if (state.kind !== "ready" && state.kind !== "personasError") return;
    const base = state.idea;
    setState({ kind: "generatingPersonas", idea: base });
    const outcome = await generatePersonas(base, props.settings, props.installedModel);
    if (outcome.kind === "error") {
      setState({ kind: "personasError", idea: base, message: outcome.message });
      return;
    }
    const updated: Idea = {
      ...base,
      personas: outcome.personas,
      critiques: [],
      updatedAtMs: Date.now(),
    };
    await putIdea(updated);
    props.onIdeaSaved();
    setState({ kind: "ready", idea: updated });
  };

  const handleGenerateCritiques = async () => {
    if (state.kind !== "ready") return;
    const base = state.idea;
    const personas = personasOf(base);
    if (personas.length === 0) return;
    setState({ kind: "generatingCritiques", idea: base, done: 0, total: personas.length });
    const { critiques, errors } = await generateCritiques(
      base,
      personas,
      props.settings,
      props.installedModel,
      (done, total) =>
        setState({ kind: "generatingCritiques", idea: base, done, total }),
    );
    const updated: Idea = {
      ...base,
      critiques,
      state: critiques.length === personas.length && base.state !== "SpecReady"
        ? "Critiqued"
        : base.state,
      updatedAtMs: Date.now(),
    };
    await putIdea(updated);
    props.onIdeaSaved();
    if (errors.length === 0) {
      setState({ kind: "ready", idea: updated });
    } else {
      setState({ kind: "critiquesPartial", idea: updated, errors });
    }
  };

  return (
    <>
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-black/5 bg-bg/90 px-4 py-3 backdrop-blur">
        <button className="btn-ghost" onClick={props.onBack}>
          Back
        </button>
        <h1 className="text-lg font-semibold tracking-tight">Personas</h1>
        <span className="w-16" />
      </header>

      <main className="flex flex-1 flex-col gap-4 px-4 py-4">
        {state.kind === "loading" && <div className="text-sm text-ink-muted">Loading…</div>}
        {state.kind === "noIdea" && (
          <div className="text-sm text-rose-700">That idea is gone.</div>
        )}

        {state.kind !== "loading" && state.kind !== "noIdea" && (
          <>
            <section className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">{state.idea.title}</h2>
              <div className="text-xs text-ink-muted">
                Engine: {activeEngineLabel(props.settings, props.installedModel)}
              </div>
            </section>

            {scanResultOf(state.idea) === undefined && (
              <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900 ring-1 ring-amber-200">
                Tip: run a scan first to ground personas in real-product context. They'll still
                generate without one, but evidence_source will be thinner.
              </div>
            )}

            <PersonaListSection state={state} onGenerate={handleGeneratePersonas} />
            <CritiqueSection state={state} onGenerate={handleGenerateCritiques} />
          </>
        )}
      </main>
    </>
  );
}

function PersonaListSection(props: {
  state: Exclude<State, { kind: "loading" } | { kind: "noIdea" }>;
  onGenerate: () => void;
}) {
  const idea = props.state.idea;
  const personas = personasOf(idea);
  const generating = props.state.kind === "generatingPersonas";

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Personas ({personas.length})</h3>
      {personas.length === 0 ? (
        <div className="card flex flex-col gap-3 p-4">
          <p className="text-sm text-ink-muted">
            One inference call returns 5 personas grounded in the scan and clarifications. Each has
            a voice signature used when they critique the idea individually.
          </p>
          <button className="btn-primary self-start" disabled={generating} onClick={props.onGenerate}>
            {generating ? "Generating…" : "Generate 5 personas"}
          </button>
          {props.state.kind === "personasError" && (
            <div className="rounded-lg bg-rose-50 p-2 text-xs text-rose-900">
              {props.state.message}
            </div>
          )}
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {personas.map((p) => (
              <li key={p.id} className="card p-3">
                <PersonaCard persona={p} critique={critiquesOf(idea).find((c) => c.personaId === p.id)} />
              </li>
            ))}
          </ul>
          <button className="btn-ghost self-start" disabled={generating} onClick={props.onGenerate}>
            {generating ? "Regenerating…" : "Regenerate personas"}
          </button>
        </>
      )}
    </section>
  );
}

function PersonaCard(props: { persona: Persona; critique?: Critique }) {
  const p = props.persona;
  const c = props.critique;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-base font-semibold">{p.name}</div>
        {c && (
          <div className="text-xs font-semibold text-accent">
            fit {c.problemFitScore}/5 · switch {c.switchLikelihoodScore}/5
          </div>
        )}
      </div>
      <div className="text-sm">{p.descriptor}</div>
      {p.evidenceSource && (
        <div className="text-xs text-ink-muted">Evidence: {p.evidenceSource}</div>
      )}
      <div className="text-xs text-ink-muted">Voice: {p.voiceSignature}</div>
      <div className="grid gap-2 text-xs sm:grid-cols-2">
        <PersonaList label="Must-haves" items={p.mustHaves} />
        <PersonaList label="Dealbreakers" items={p.dealbreakers} />
      </div>
      {c && (
        <div className="mt-2 rounded-lg bg-accent-soft p-2 text-xs">
          <div className="font-semibold uppercase tracking-wide text-accent">Reaction</div>
          <div className="mt-1 whitespace-pre-wrap text-ink">{c.reactionSummary}</div>
          {c.questionsBack.length > 0 && (
            <div className="mt-2">
              <div className="font-semibold uppercase tracking-wide text-accent">
                Questions back
              </div>
              <ul className="mt-1 list-disc space-y-1 pl-4 text-ink">
                {c.questionsBack.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <PersonaList label="Must-haves (critique)" items={c.mustHaves} />
            <PersonaList label="Dealbreakers (critique)" items={c.dealbreakers} />
          </div>
        </div>
      )}
    </div>
  );
}

function PersonaList(props: { label: string; items: string[] }) {
  if (props.items.length === 0) return null;
  return (
    <div>
      <div className="font-semibold uppercase tracking-wide text-ink-muted">{props.label}</div>
      <ul className="list-disc space-y-0.5 pl-4">
        {props.items.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ul>
    </div>
  );
}

function CritiqueSection(props: {
  state: Exclude<State, { kind: "loading" } | { kind: "noIdea" }>;
  onGenerate: () => void;
}) {
  const idea = props.state.idea;
  const personas = personasOf(idea);
  if (personas.length === 0) return null;

  const critiques = critiquesOf(idea);
  if (props.state.kind === "generatingCritiques") {
    return (
      <div className="card flex flex-col gap-2 p-4">
        <div className="text-sm font-medium">
          Running persona critiques… {props.state.done}/{props.state.total}
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-black/5">
          <div
            className="h-full bg-accent transition-[width]"
            style={{
              width:
                props.state.total > 0
                  ? `${Math.round((props.state.done / props.state.total) * 100)}%`
                  : "20%",
            }}
          />
        </div>
        <div className="text-xs text-ink-muted">
          Cloud runs all personas in parallel. On-device runs them one at a time.
        </div>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">
        Critiques ({critiques.length}/{personas.length})
      </h3>
      <div className="card flex flex-col gap-3 p-4">
        {critiques.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Each persona reacts in their own voice — problem-fit score, switch-likelihood,
            dealbreakers, must-haves, and Mom-Test questions back.
          </p>
        ) : (
          <p className="text-sm text-ink-muted">
            {critiques.length === personas.length
              ? "All personas have weighed in. See per-persona reaction inline above."
              : `${critiques.length} of ${personas.length} done — some calls failed.`}
          </p>
        )}
        <button className="btn-primary self-start" onClick={props.onGenerate}>
          {critiques.length === 0 ? "Run critiques" : "Re-run critiques"}
        </button>
        {props.state.kind === "critiquesPartial" && props.state.errors.length > 0 && (
          <div className="rounded-lg bg-rose-50 p-2 text-xs text-rose-900">
            {props.state.errors.length} persona{props.state.errors.length === 1 ? "" : "s"}{" "}
            errored. Tap retry to re-run any failures (they'll go through again).
          </div>
        )}
      </div>
    </section>
  );
}
